/**
 * Performance metrics data structure
 */
export interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  updateTime: number;
  renderTime: number;
  collisionTime: number;
  systemTimes: Map<string, number>;
  memoryUsage?: number;
  objectCounts: Map<string, number>;
  customMetrics: Map<string, number>;
}

/**
 * Performance monitoring utility for tracking engine performance
 */
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;

  private metrics: PerformanceMetrics;
  private frameStartTime: number = 0;
  private lastFrameTime: number = 0;
  private frameCount: number = 0;
  private fpsBuffer: number[] = [];
  private readonly FPS_BUFFER_SIZE = 60;
  private systemStartTimes: Map<string, number> = new Map();

  private constructor() {
    this.metrics = {
      fps: 0,
      frameTime: 0,
      updateTime: 0,
      renderTime: 0,
      collisionTime: 0,
      systemTimes: new Map(),
      objectCounts: new Map(),
      customMetrics: new Map(),
    };
  }

  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Start frame timing
   */
  public startFrame(): void {
    this.frameStartTime = performance.now();
    this.frameCount++;
  }

  /**
   * End frame timing and calculate FPS
   */
  public endFrame(): void {
    const currentTime = performance.now();
    const frameTime = currentTime - this.frameStartTime;

    this.metrics.frameTime = frameTime;

    // Calculate FPS using a moving average
    this.fpsBuffer.push(1000 / frameTime);
    if (this.fpsBuffer.length > this.FPS_BUFFER_SIZE) {
      this.fpsBuffer.shift();
    }

    this.metrics.fps =
      this.fpsBuffer.reduce((a, b) => a + b, 0) / this.fpsBuffer.length;
    this.lastFrameTime = currentTime;
  }

  /**
   * Start timing a specific system
   */
  public startSystem(systemName: string): void {
    this.systemStartTimes.set(systemName, performance.now());
  }

  /**
   * End timing a specific system
   */
  public endSystem(systemName: string): void {
    const startTime = this.systemStartTimes.get(systemName);
    if (startTime !== undefined) {
      const duration = performance.now() - startTime;
      this.metrics.systemTimes.set(systemName, duration);
      this.systemStartTimes.delete(systemName);

      // Update specific timing categories
      switch (systemName.toLowerCase()) {
        case 'collision':
        case 'collisionsystem':
        case 'advancedcollisionsystem':
          this.metrics.collisionTime = duration;
          break;
        case 'render':
        case 'rendering':
          this.metrics.renderTime = duration;
          break;
        case 'update':
          this.metrics.updateTime = duration;
          break;
      }
    }
  }

  /**
   * Set object count for a specific type
   */
  public setObjectCount(objectType: string, count: number): void {
    this.metrics.objectCounts.set(objectType, count);
  }

  /**
   * Set a custom metric
   */
  public setCustomMetric(name: string, value: number): void {
    this.metrics.customMetrics.set(name, value);
  }

  /**
   * Get current performance metrics
   */
  public getMetrics(): PerformanceMetrics {
    // Update memory usage if available
    if ((performance as any).memory) {
      this.metrics.memoryUsage =
        (performance as any).memory.usedJSHeapSize / 1024 / 1024; // MB
    }

    return {
      fps: Math.round(this.metrics.fps * 100) / 100,
      frameTime: Math.round(this.metrics.frameTime * 100) / 100,
      updateTime: Math.round(this.metrics.updateTime * 100) / 100,
      renderTime: Math.round(this.metrics.renderTime * 100) / 100,
      collisionTime: Math.round(this.metrics.collisionTime * 100) / 100,
      systemTimes: new Map(this.metrics.systemTimes),
      memoryUsage: this.metrics.memoryUsage,
      objectCounts: new Map(this.metrics.objectCounts),
      customMetrics: new Map(this.metrics.customMetrics),
    };
  }

  /**
   * Get a formatted string of performance metrics
   */
  public getFormattedMetrics(): string {
    const metrics = this.getMetrics();
    let output = `FPS: ${metrics.fps.toFixed(
      1
    )} | Frame: ${metrics.frameTime.toFixed(2)}ms`;

    if (metrics.updateTime > 0) {
      output += ` | Update: ${metrics.updateTime.toFixed(2)}ms`;
    }

    if (metrics.renderTime > 0) {
      output += ` | Render: ${metrics.renderTime.toFixed(2)}ms`;
    }

    if (metrics.collisionTime > 0) {
      output += ` | Collision: ${metrics.collisionTime.toFixed(2)}ms`;
    }

    if (metrics.memoryUsage) {
      output += ` | Memory: ${metrics.memoryUsage.toFixed(1)}MB`;
    }

    // Add object counts
    if (metrics.objectCounts.size > 0) {
      const counts = Array.from(metrics.objectCounts.entries())
        .map(([type, count]) => `${type}: ${count}`)
        .join(', ');
      output += ` | Objects: ${counts}`;
    }

    return output;
  }

  /**
   * Reset all metrics
   */
  public reset(): void {
    this.metrics = {
      fps: 0,
      frameTime: 0,
      updateTime: 0,
      renderTime: 0,
      collisionTime: 0,
      systemTimes: new Map(),
      objectCounts: new Map(),
      customMetrics: new Map(),
    };
    this.fpsBuffer = [];
    this.systemStartTimes.clear();
    this.frameCount = 0;
  }

  /**
   * Get performance statistics over time
   */
  public getStats(): {
    averageFps: number;
    minFps: number;
    maxFps: number;
    frameCount: number;
  } {
    const minFps = this.fpsBuffer.length > 0 ? Math.min(...this.fpsBuffer) : 0;
    const maxFps = this.fpsBuffer.length > 0 ? Math.max(...this.fpsBuffer) : 0;
    const averageFps =
      this.fpsBuffer.length > 0
        ? this.fpsBuffer.reduce((a, b) => a + b, 0) / this.fpsBuffer.length
        : 0;

    return {
      averageFps: Math.round(averageFps * 100) / 100,
      minFps: Math.round(minFps * 100) / 100,
      maxFps: Math.round(maxFps * 100) / 100,
      frameCount: this.frameCount,
    };
  }

  /**
   * Check if performance is below acceptable thresholds
   */
  public isPerformancePoor(targetFps: number = 60): boolean {
    return this.metrics.fps < targetFps * 0.8; // 80% of target FPS
  }

  /**
   * Get performance warnings
   */
  public getWarnings(targetFps: number = 60): string[] {
    const warnings: string[] = [];
    const metrics = this.getMetrics();

    if (metrics.fps < targetFps * 0.8) {
      warnings.push(
        `Low FPS: ${metrics.fps.toFixed(1)} (target: ${targetFps})`
      );
    }

    if (metrics.frameTime > (1000 / targetFps) * 1.5) {
      warnings.push(`High frame time: ${metrics.frameTime.toFixed(2)}ms`);
    }

    if (metrics.collisionTime > 5) {
      warnings.push(
        `High collision time: ${metrics.collisionTime.toFixed(2)}ms`
      );
    }

    if (metrics.memoryUsage && metrics.memoryUsage > 100) {
      warnings.push(`High memory usage: ${metrics.memoryUsage.toFixed(1)}MB`);
    }

    // Check for slow systems
    for (const [systemName, time] of metrics.systemTimes) {
      if (time > 10) {
        warnings.push(`Slow system '${systemName}': ${time.toFixed(2)}ms`);
      }
    }

    return warnings;
  }
}
